/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from "react";
import { motion } from "framer-motion";

const apple = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif";
const ORANGE = "#ff6b00";
const GREEN = "#10b981";

const BASE = typeof window !== "undefined" ? window.location.origin : "";

export default function OnePager() {
  useEffect(() => {
    document.title = "RY Xpress — Digital Freight Platform Proposal";
    document.body.style.background = "#0a0a0c";
    document.body.style.margin = "0";
    document.body.style.fontFamily = apple;
    document.body.style.color = "white";
    return () => { document.body.style.background = ""; document.body.style.margin = ""; };
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #0a0a0c 0%, #0f0f12 100%)", color: "white", fontFamily: apple, position: "relative", overflowX: "hidden" }}>
      <Backdrop />
      <Nav />

      <Hero />
      <Why />
      <Retention />
      <Automation />
      <RevenueCenters />
      <Scalability />
      <DemoLinks />
      <ClosingCTA />
      <Footer />
    </div>
  );
}

/* ============== Reusable bits ============== */
function Fade({ children, delay = 0, y = 14 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.2, 0.7, 0.3, 1] }}>
      {children}
    </motion.div>
  );
}

function Section({ children, style = {}, id }) {
  return (
    <section id={id} style={{ maxWidth: 1120, margin: "0 auto", padding: "80px 32px", position: "relative", zIndex: 1, ...style }}>{children}</section>
  );
}

function Kicker({ children, color = ORANGE }) {
  return <div style={{ fontSize: 12, letterSpacing: 3, color, fontWeight: 700, textTransform: "uppercase" }}>{children}</div>;
}

function H2({ children }) {
  return <h2 style={{ fontSize: "clamp(32px, 4.5vw, 52px)", fontWeight: 800, letterSpacing: -1.5, lineHeight: 1.05, margin: "12px 0 18px", color: "white" }}>{children}</h2>;
}

function Lead({ children }) {
  return <p style={{ fontSize: 18, lineHeight: 1.55, color: "rgba(255,255,255,0.72)", maxWidth: 720, margin: 0, fontWeight: 400 }}>{children}</p>;
}

/* ============== Background ornaments ============== */
function Backdrop() {
  return (
    <>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", opacity: 0.6, backgroundImage: `radial-gradient(at 20% 0%, rgba(255,107,0,0.18), transparent 50%), radial-gradient(at 80% 30%, rgba(168,85,247,0.12), transparent 50%), radial-gradient(at 50% 100%, rgba(59,130,246,0.10), transparent 50%)` }} />
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", opacity: 0.025, backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`, backgroundSize: "44px 44px" }} />
    </>
  );
}

/* ============== Nav ============== */
function Nav() {
  return (
    <nav style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(10,10,12,0.7)", backdropFilter: "blur(20px)", borderBottom: "0.5px solid rgba(255,255,255,0.08)" }}>
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "14px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 26, fontWeight: 900, color: ORANGE, fontStyle: "italic", letterSpacing: -1, lineHeight: 1 }}>RY</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "white", letterSpacing: 3, fontStyle: "italic" }}>XPRESS</span>
          <span style={{ fontSize: 10.5, color: "rgba(255,255,255,0.45)", marginLeft: 8, letterSpacing: 1.2 }}>· PROPOSAL</span>
        </div>
        <a href="#demos" style={{ background: ORANGE, color: "white", padding: "8px 16px", borderRadius: 999, fontSize: 12.5, fontWeight: 700, textDecoration: "none", letterSpacing: 0.3 }}>Try the demos →</a>
      </div>
    </nav>
  );
}

/* ============== Hero ============== */
function Hero() {
  return (
    <Section style={{ padding: "100px 32px 60px", textAlign: "left" }}>
      <Fade>
        <Kicker>FOR RY XPRESS · CONFIDENTIAL</Kicker>
      </Fade>
      <Fade delay={0.08}>
        <h1 style={{ fontSize: "clamp(44px, 7vw, 88px)", fontWeight: 900, letterSpacing: -3, lineHeight: 0.98, margin: "16px 0 22px", color: "white" }}>
          The operating system <br />
          for a freight brokerage that <br />
          <span style={{ background: `linear-gradient(135deg, ${ORANGE}, #ff9a4c)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>doesn't sleep.</span>
        </h1>
      </Fade>
      <Fade delay={0.18}>
        <p style={{ fontSize: 20, lineHeight: 1.5, color: "rgba(255,255,255,0.78)", maxWidth: 720, margin: "0 0 32px", fontWeight: 400 }}>
          A purpose-built digital platform for RY Xpress — Customer App, Driver App, and a full-stack admin command center.
          AI-priced quotes, reverse-bid load board, live ops, automated payouts, and brand-new recurring revenue streams.
        </p>
      </Fade>
      <Fade delay={0.28}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 60 }}>
          <a href="#demos" style={{ background: ORANGE, color: "white", padding: "14px 24px", borderRadius: 12, fontSize: 14, fontWeight: 700, textDecoration: "none", boxShadow: `0 14px 36px ${ORANGE}55` }}>See it live →</a>
          <a href="#why" style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.15)", color: "white", padding: "14px 24px", borderRadius: 12, fontSize: 14, fontWeight: 700, textDecoration: "none" }}>Why this matters</a>
        </div>
      </Fade>
      <Fade delay={0.4}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, paddingTop: 32, borderTop: "0.5px solid rgba(255,255,255,0.08)" }}>
          {[
            { v: "70%", l: "less time per dispatch" },
            { v: "+4.2pt", l: "margin via reverse bidding" },
            { v: "6", l: "new recurring revenue lines" },
            { v: "24/7", l: "self-serve customer + driver" },
          ].map((s) => (
            <div key={s.l}>
              <div style={{ fontSize: 32, fontWeight: 800, color: ORANGE, letterSpacing: -1.5 }}>{s.v}</div>
              <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </Fade>
    </Section>
  );
}

/* ============== Why ============== */
function Why() {
  return (
    <Section id="why">
      <Fade><Kicker>WHY AN APP</Kicker></Fade>
      <Fade delay={0.05}><H2>The brokerages winning right now own their tech stack.</H2></Fade>
      <Fade delay={0.1}><Lead>Phone-and-spreadsheet brokerages are losing drivers, customers, and margin to platforms like Convoy, Uber Freight, and DAT. A custom RY-branded app puts you on equal footing — and unlocks revenue streams a generic load board can't touch.</Lead></Fade>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginTop: 40 }}>
        {[
          { t: "Drivers expect an app", b: "If your competitor has one and you don't, the driver picks the competitor's load.", icon: "📱" },
          { t: "Customers want self-serve", b: "Shippers book and track from their phone at 11pm without calling you. You sleep.", icon: "🌙" },
          { t: "Brokerage is becoming software", b: "Whoever automates fastest captures the margin. Manual processes = lost shipments.", icon: "⚡" },
        ].map((c, i) => (
          <Fade key={c.t} delay={0.1 + i * 0.08}>
            <Card><div style={{ fontSize: 26, marginBottom: 10 }}>{c.icon}</div><div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{c.t}</div><div style={{ fontSize: 13.5, color: "rgba(255,255,255,0.65)", lineHeight: 1.55 }}>{c.b}</div></Card>
          </Fade>
        ))}
      </div>
    </Section>
  );
}

/* ============== Retention ============== */
function Retention() {
  return (
    <Section>
      <Fade><Kicker>DRIVER + CUSTOMER RETENTION</Kicker></Fade>
      <Fade delay={0.05}><H2>Lock them in. Make leaving feel expensive.</H2></Fade>
      <Fade delay={0.1}><Lead>Every interaction inside the RY app is a switching cost. Drivers build payment history, ratings, and savings inside the app. Customers build addresses, contacts, lanes, and POD archives. Leaving = starting from zero.</Lead></Fade>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 40 }}>
        <Fade delay={0.1}>
          <BigCard accent={ORANGE} label="DRIVERS">
            <h3 style={{ fontSize: 24, margin: "0 0 14px", fontWeight: 800, letterSpacing: -0.6 }}>Why drivers stay</h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 14, lineHeight: 1.85, color: "rgba(255,255,255,0.85)" }}>
              <Li>Instant pay (no waiting on factoring or net-30)</Li>
              <Li>Reverse-bid the loads they actually want</Li>
              <Li>RY+ Gold membership: free instant pay, 4% fuel rebate, priority dispatch</Li>
              <Li>Full earnings, mileage, and tax history in one tap</Li>
              <Li>Roadside-mechanic button: tap once, help comes</Li>
              <Li>BOL, POD, photos all upload from phone — no paper</Li>
            </ul>
          </BigCard>
        </Fade>
        <Fade delay={0.18}>
          <BigCard accent="#3b82f6" label="CUSTOMERS">
            <h3 style={{ fontSize: 24, margin: "0 0 14px", fontWeight: 800, letterSpacing: -0.6 }}>Why shippers stay</h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 14, lineHeight: 1.85, color: "rgba(255,255,255,0.85)" }}>
              <Li>Self-serve AI quote in 5 seconds, 24/7</Li>
              <Li>Book + track + sign POD without a phone call</Li>
              <Li>Saved lanes, contacts, billing — re-booking takes 1 tap</Li>
              <Li>Live map: see exactly where your freight is</Li>
              <Li>Auto-invoiced on delivery, paid by ACH or card in-app</Li>
              <Li>Historical lane data: "this lane averages $2,265 — your quote is fair"</Li>
            </ul>
          </BigCard>
        </Fade>
      </div>
    </Section>
  );
}

/* ============== Automation ============== */
function Automation() {
  const tasks = [
    { t: "Quoting", manual: "12 min calls + emails", auto: "5 sec AI quote, lane-history priced" },
    { t: "Load assignment", manual: "Phone tag with 8 drivers", auto: "Drivers bid in-app, best margin wins" },
    { t: "Status updates", manual: "Hourly check-in calls", auto: "Auto-pushed from driver GPS + photos" },
    { t: "BOL / POD handling", manual: "Email, scan, file", auto: "Driver snaps photo, customer signs in app" },
    { t: "Invoicing", manual: "Manual AR clerk entry", auto: "Auto-generated on POD upload" },
    { t: "Payouts", manual: "Wednesday wire batch + factoring fees", auto: "Instant pay button, scheduled ACH, deductions automated" },
    { t: "Compliance docs", manual: "Tracked in spreadsheets", auto: "Driver app pings 30 days before expiry" },
    { t: "Claims", manual: "Calls + paper trail", auto: "Photos + GPS + audit log baked in" },
  ];
  return (
    <Section>
      <Fade><Kicker>AI + AUTOMATION</Kicker></Fade>
      <Fade delay={0.05}><H2>Same revenue. Half the staff hours.</H2></Fade>
      <Fade delay={0.1}><Lead>What used to take a dispatcher 12 minutes now takes the platform 5 seconds. Multiply that across 200 loads a week and you're either freeing up your team for higher-value work, or running 2× the volume with the same headcount.</Lead></Fade>

      <Fade delay={0.15}>
        <div style={{ marginTop: 40, background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.5fr", padding: "14px 20px", fontSize: 10.5, letterSpacing: 1.5, color: "rgba(255,255,255,0.5)", fontWeight: 700, borderBottom: "0.5px solid rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.2)" }}>
            <div>TASK</div><div>OLD WAY</div><div>RY APP WAY</div>
          </div>
          {tasks.map((t, i) => (
            <div key={t.t} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.5fr", padding: "14px 20px", borderBottom: i === tasks.length - 1 ? "none" : "0.5px solid rgba(255,255,255,0.04)", fontSize: 13.5, alignItems: "center" }}>
              <div style={{ fontWeight: 700, color: "white" }}>{t.t}</div>
              <div style={{ color: "rgba(255,255,255,0.5)", textDecoration: "line-through", textDecorationColor: "rgba(255,107,0,0.5)" }}>{t.manual}</div>
              <div style={{ color: GREEN, fontWeight: 600 }}>✓ {t.auto}</div>
            </div>
          ))}
        </div>
      </Fade>
    </Section>
  );
}

/* ============== Revenue Centers ============== */
function RevenueCenters() {
  const centers = [
    { t: "Trucks & equipment", b: "RY-branded marketplace: new and used trucks, trailers, reefers. Take a commission, finance the deal, or rent-to-own.", emoji: "🚛", color: ORANGE },
    { t: "Vehicle financing", b: "In-app loan applications for drivers buying trucks. Partner with a lender, earn origination fees + a slice of interest.", emoji: "💳", color: "#10b981" },
    { t: "Repair & roadside", b: "One-tap 'help me' button. Roadside mechanic dispatched, billed through the app. RY takes a network fee on every call.", emoji: "🔧", color: "#f59e0b" },
    { t: "Parts catalog", b: "OEM and aftermarket parts ordered from the driver app, shipped to next stop. Margin on every brake pad, tire, oil change.", emoji: "🔩", color: "#3b82f6" },
    { t: "Insurance", b: "Cargo, liability, occupational accident. Group buy with the RY driver pool → cheaper for drivers, premium revenue + claim-ratio float for RY.", emoji: "🛡️", color: "#a855f7" },
    { t: "RY+ Memberships", b: "Driver subscription ($49–$99/mo) for instant pay, fuel rebates, priority dispatch. Pure recurring MRR with 92% retention.", emoji: "⭐", color: "#ff6b00" },
    { t: "Fuel card program", b: "Co-branded fuel card with RY. Skim a few cents per gallon rebate, sell premium card tiers, surface live fuel savings in-app.", emoji: "⛽", color: "#ef4444" },
    { t: "Factoring (in-house)", b: "Skip the third-party factor. Offer 24-hour funding on invoices, charge 1–2% fee, retain the float.", emoji: "💰", color: "#10b981" },
  ];
  return (
    <Section>
      <Fade><Kicker color="#10b981">NEW REVENUE CENTERS</Kicker></Fade>
      <Fade delay={0.05}><H2>The app pays for itself <span style={{ color: GREEN }}>8 ways.</span></H2></Fade>
      <Fade delay={0.1}><Lead>Every driver and customer using the platform is sitting next to a high-margin product they actually need. Brokerage rates are commoditized — these aren't.</Lead></Fade>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginTop: 40 }}>
        {centers.map((c, i) => (
          <Fade key={c.t} delay={0.05 + (i % 4) * 0.06}>
            <div style={{ background: "rgba(255,255,255,0.025)", border: `0.5px solid ${c.color}33`, borderRadius: 14, padding: 20, position: "relative", overflow: "hidden", height: "100%" }}>
              <div style={{ position: "absolute", top: -30, right: -30, width: 100, height: 100, borderRadius: "50%", background: `radial-gradient(circle, ${c.color}33, transparent 70%)` }} />
              <div style={{ fontSize: 32, marginBottom: 12, position: "relative" }}>{c.emoji}</div>
              <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 8, position: "relative", color: "white", letterSpacing: -0.3 }}>{c.t}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", lineHeight: 1.55, position: "relative" }}>{c.b}</div>
            </div>
          </Fade>
        ))}
      </div>

      <Fade delay={0.3}>
        <div style={{ marginTop: 36, background: `linear-gradient(135deg, ${ORANGE}11, ${GREEN}11)`, border: `0.5px solid ${ORANGE}44`, borderRadius: 14, padding: 24 }}>
          <div style={{ fontSize: 12, letterSpacing: 1.5, color: ORANGE, fontWeight: 700, marginBottom: 8 }}>THE MULTIPLIER EFFECT</div>
          <div style={{ fontSize: 18, color: "white", lineHeight: 1.55, fontWeight: 500 }}>
            A single driver doing $120K/yr in brokered freight can also be: a $99/mo RY+ member, a fuel-card user (~$24K/yr fuel through RY), a parts buyer ($3-8K/yr), an insurance customer ($6-10K/yr premium), and a potential truck-finance customer. <strong style={{ color: ORANGE }}>One acquisition, lifetime value 3-5×.</strong>
          </div>
        </div>
      </Fade>
    </Section>
  );
}

/* ============== Scalability ============== */
function Scalability() {
  return (
    <Section>
      <Fade><Kicker color="#3b82f6">SCALABILITY</Kicker></Fade>
      <Fade delay={0.05}><H2>Built to handle 10× the volume without 10× the team.</H2></Fade>
      <Fade delay={0.1}><Lead>The whole point: revenue grows linearly with shipments, but staff cost grows logarithmically. The platform is the leverage.</Lead></Fade>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginTop: 40 }}>
        {[
          { t: "Geo expansion", b: "Add Vancouver, Calgary, Montreal as RY zones — same platform, same drivers can opt in. No new dispatch offices needed." },
          { t: "Sub-brokerage", b: "License a white-labeled version of the platform to smaller brokerages. Recurring SaaS revenue on top of freight." },
          { t: "Cross-border", b: "ACE/ACI eManifest built in. Adding US-MX or US-CA lanes is a settings toggle, not a 6-month project." },
          { t: "Multi-modal", b: "Same UI handles dry van, reefer, flatbed, intermodal, LTL. Add equipment types without rebuilding workflows." },
          { t: "Data moat", b: "Every load priced by AI adds to the model. After 12 months, your pricing is sharper than any competitor's." },
          { t: "Acquisition-ready", b: "A clean tech stack + recurring memberships + multi-revenue lines = 4-8× higher exit valuation than a phone-and-fax brokerage." },
        ].map((c, i) => (
          <Fade key={c.t} delay={0.05 + (i % 3) * 0.05}>
            <Card><div style={{ fontSize: 15, fontWeight: 800, marginBottom: 6, color: "white" }}>{c.t}</div><div style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", lineHeight: 1.55 }}>{c.b}</div></Card>
          </Fade>
        ))}
      </div>
    </Section>
  );
}

/* ============== Demo Links ============== */
function DemoLinks() {
  const demos = [
    { t: "RY Driver", sub: "Mobile · Driver-facing", desc: "Bid loads, instant pay, RY+ benefits, roadside-mechanic button, BOL/POD upload.", href: "/driver", img: "/assets/ry-driver.png", color: ORANGE },
    { t: "RY Ship", sub: "Mobile · Customer-facing", desc: "AI-quoted shipping for shippers. Book, track, sign POD, pay invoices, all from phone.", href: "/ship", img: "/assets/ry-ship.png", color: "#3b82f6" },
    { t: "RY Command", sub: "Mobile · Admin", desc: "On-the-go admin: dispatch, live fleet, exception queue, invoices, payouts. For ops on the road.", href: "/command", img: "/assets/ry-command.png", color: "#a855f7" },
    { t: "RY Command Web", sub: "Desktop · Full admin", desc: "The brokerage's full operating system. 23 modules: dispatch, AI pricing, bidding, CRM, finance, claims, audit log.", href: "/web", img: "/assets/ry-command.png", color: GREEN },
  ];
  return (
    <Section id="demos">
      <Fade><Kicker>SEE IT LIVE</Kicker></Fade>
      <Fade delay={0.05}><H2>Click into any of the 4 apps.</H2></Fade>
      <Fade delay={0.1}><Lead>These are fully interactive product mockups — clickable, animated, populated with realistic RY-themed data. Share these links with anyone on your team.</Lead></Fade>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginTop: 40 }}>
        {demos.map((d, i) => (
          <Fade key={d.t} delay={0.1 + i * 0.07}>
            <a href={d.href} target="_blank" rel="noreferrer"
              style={{ textDecoration: "none", color: "white", display: "block", background: "rgba(255,255,255,0.025)", border: `0.5px solid ${d.color}44`, borderRadius: 16, padding: 22, transition: "all 200ms cubic-bezier(.2,.7,.3,1)", position: "relative", overflow: "hidden", height: "100%" }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.borderColor = d.color; e.currentTarget.style.boxShadow = `0 20px 50px ${d.color}33`; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderColor = `${d.color}44`; e.currentTarget.style.boxShadow = ""; }}>
              <div style={{ position: "absolute", top: -20, right: -20, width: 120, height: 120, borderRadius: "50%", background: `radial-gradient(circle, ${d.color}33, transparent 70%)` }} />
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start", position: "relative" }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, overflow: "hidden", background: `linear-gradient(135deg, ${d.color}, ${d.color}aa)`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img src={d.img} alt={d.t} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, letterSpacing: 1.5, color: d.color, fontWeight: 700 }}>{d.sub.toUpperCase()}</div>
                  <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: -0.5, marginTop: 2 }}>{d.t}</div>
                </div>
              </div>
              <div style={{ fontSize: 13.5, color: "rgba(255,255,255,0.65)", marginTop: 14, lineHeight: 1.5, position: "relative" }}>{d.desc}</div>
              <div style={{ marginTop: 16, fontSize: 12, color: d.color, fontWeight: 700, display: "flex", alignItems: "center", gap: 4, position: "relative" }}>
                Open demo <span style={{ transition: "transform 200ms" }}>→</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 10.5, color: "rgba(255,255,255,0.4)", fontFamily: "JetBrains Mono, monospace", position: "relative" }}>{BASE}{d.href}</div>
            </a>
          </Fade>
        ))}
      </div>

      <Fade delay={0.4}>
        <div style={{ marginTop: 28, padding: 16, background: "rgba(255,255,255,0.02)", border: "0.5px dashed rgba(255,255,255,0.15)", borderRadius: 12, textAlign: "center", fontSize: 12.5, color: "rgba(255,255,255,0.6)" }}>
          Bonus: the <a href="/" style={{ color: ORANGE, fontWeight: 700 }}>full proposal experience</a> at <code style={{ background: "rgba(255,255,255,0.05)", padding: "2px 6px", borderRadius: 4 }}>{BASE}/</code> — an interactive iPhone you can unlock (passcode <strong>1234</strong>) and play with directly.
        </div>
      </Fade>
    </Section>
  );
}

/* ============== Closing CTA ============== */
function ClosingCTA() {
  return (
    <Section>
      <Fade>
        <div style={{ background: `linear-gradient(135deg, ${ORANGE}, #d14d00)`, borderRadius: 24, padding: "56px 40px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -100, right: -100, width: 300, height: 300, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
          <div style={{ position: "absolute", bottom: -80, left: -80, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
          <div style={{ position: "relative", maxWidth: 680 }}>
            <div style={{ fontSize: 12, letterSpacing: 2.5, color: "rgba(255,255,255,0.85)", fontWeight: 700 }}>NEXT STEPS</div>
            <h2 style={{ fontSize: "clamp(32px, 5vw, 52px)", fontWeight: 900, letterSpacing: -1.5, margin: "12px 0 18px", lineHeight: 1.05, color: "white" }}>
              Let's spend 30 minutes mapping this to RY's actual book of business.
            </h2>
            <p style={{ fontSize: 17, color: "rgba(255,255,255,0.92)", margin: "0 0 28px", lineHeight: 1.55 }}>
              Walk me through your current volume, lanes, and driver pool — I'll come back with: timeline, build cost, projected revenue lift, and the first quarter milestone.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              <a href="mailto:cameron@meinhaus.co?subject=RY%20Xpress%20Digital%20Platform" style={{ background: "white", color: ORANGE, padding: "14px 24px", borderRadius: 12, fontSize: 14, fontWeight: 800, textDecoration: "none" }}>Email Cameron @ Meinhaus →</a>
              <a href="#demos" style={{ background: "rgba(0,0,0,0.18)", border: "1px solid rgba(255,255,255,0.3)", color: "white", padding: "14px 24px", borderRadius: 12, fontSize: 14, fontWeight: 700, textDecoration: "none" }}>Review demos again</a>
            </div>
          </div>
        </div>
      </Fade>
    </Section>
  );
}

/* ============== Footer ============== */
function Footer() {
  return (
    <footer style={{ borderTop: "0.5px solid rgba(255,255,255,0.06)", padding: "32px", textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: 12, marginTop: 40 }}>
      Designed and built by <a href="https://meinhaus.co" style={{ color: ORANGE, fontWeight: 700, textDecoration: "none" }}>Meinhaus</a> for RY Xpress Transportation & Logistics · {new Date().getFullYear()}
    </footer>
  );
}

/* ============== Sub-components ============== */
function Card({ children }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 20, height: "100%" }}>{children}</div>
  );
}
function BigCard({ children, accent, label }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: `0.5px solid ${accent}44`, borderRadius: 18, padding: 28, height: "100%", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -50, right: -50, width: 180, height: 180, borderRadius: "50%", background: `radial-gradient(circle, ${accent}22, transparent 70%)` }} />
      <div style={{ fontSize: 11, letterSpacing: 2, color: accent, fontWeight: 700, marginBottom: 8, position: "relative" }}>{label}</div>
      <div style={{ position: "relative" }}>{children}</div>
    </div>
  );
}
function Li({ children }) {
  return (
    <li style={{ paddingLeft: 22, position: "relative", marginBottom: 6 }}>
      <span style={{ position: "absolute", left: 0, top: 0, color: ORANGE, fontWeight: 800 }}>›</span>
      {children}
    </li>
  );
}
